import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
} from '@/shared/components';
import { toApiError, useApi } from '@/shared/api';

interface ConnectChannelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channexPropertyId: string;
  currency: string;
  channelsQueryKey: readonly unknown[];
  /** Ohne Mapping-Fenster ordnet der StayOn-Support Zimmer und Raten zu, nicht der Kunde. */
  canOpenMapping: boolean;
}

export function ConnectChannelDialog({
  open,
  onOpenChange,
  channexPropertyId,
  currency,
  channelsQueryKey,
  canOpenMapping,
}: ConnectChannelDialogProps) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [hotelId, setHotelId] = useState('');
  const [title, setTitle] = useState('Booking.com');

  const connect = useMutation({
    mutationFn: () =>
      api.channex.createChannel({
        title,
        channel: 'BookingCom',
        currency,
        hotelId,
        channexPropertyId,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: channelsQueryKey });
      toast.success(
        canOpenMapping
          ? 'Booking.com verbunden. Ordnen Sie als Nächstes im Mapping-Fenster Zimmer und Raten zu.'
          : 'Booking.com verbunden. Zimmer und Raten ordnet als Nächstes der StayOn-Support zu.',
      );
      onOpenChange(false);
      setHotelId('');
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Booking.com verbinden</DialogTitle>
          <DialogDescription>
            Die Hotel-ID finden Sie im Booking.com-Extranet unter „Allgemeine Infos“, bei
            Ferienwohnungen ist es die Unterkunfts-ID.{' '}
            {canOpenMapping
              ? 'Zimmer und Raten ordnen Sie danach im Mapping-Fenster zu.'
              : 'Zimmer und Raten ordnet danach der StayOn-Support zu.'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <FormField label="Hotel-ID" required>
            <Input
              value={hotelId}
              onChange={(e) => setHotelId(e.target.value)}
              placeholder="z.B. 12345678"
            />
          </FormField>
          <FormField label="Anzeige-Titel">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </FormField>
          <div className="mt-6 flex justify-end gap-3">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button
              variant="brand"
              loading={connect.isPending}
              disabled={!hotelId.trim()}
              onClick={() => connect.mutate()}
            >
              Verbinden
            </Button>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
